/**
 * The audit action vocabulary, grouped for people.
 *
 * ── WHY THIS IS ONE LIST AND NOT TWO ──
 * Two consumers have to agree about these strings: the server compiles a
 * category chip into a SQL predicate, and the web app renders each action as a
 * phrase somebody can read. Keeping the phrasing in a component and the
 * grouping in a service is how they drift - FormDesk shipped exactly that and
 * wrote down what happened: the label map covered sixteen of the thirty-one
 * actions being written, so the rest rendered as raw slugs, and nobody noticed
 * because the missing ones were the newest features.
 *
 * Fint had the same problem in a blunter form. It writes forty-odd actions and
 * labelled none of them, so every row on the Activity page read
 * `bank_transaction.categorised`.
 *
 * ── THE RAW ACTION IS STILL SHOWN ──
 * A phrase is what makes the page scannable; the slug is what a CA quotes in an
 * email when they ask why a figure moved. The page shows both, so adding
 * phrasing takes nothing away.
 */
export const AUDIT_CATEGORIES = [
    { key: 'documents', label: 'Invoices & proformas' },
    { key: 'money', label: 'Money in & out' },
    { key: 'records', label: 'Customers, vendors & items' },
    /**
     * Its own group rather than a member of `admin`, because "who locked the
     * period" and "who changed a tax rate" are the two questions an accountant
     * asks first, and burying them among role edits answers neither.
     */
    { key: 'filing', label: 'Periods & tax' },
    { key: 'admin', label: 'People & settings' },
    /**
     * What Claude did. Off by default on the page, on by one click.
     *
     * A model doing an hour's work leaves far more rows than a person does, and
     * mixing them into the everyday feed would bury the figure that changed under
     * fifty reads. Separating them is what makes it safe to record reads at all -
     * see `recordCall` in mcp.service.ts.
     */
    { key: 'assistant', label: 'AI agent' },
];
/**
 * Action to category. Every action the API writes belongs here.
 *
 * Anything missing is treated as `admin` at query time (see
 * `actionsForCategories`), so a newly added action degrades to "visible in one
 * group" rather than "invisible in every filtered view" - which is the failure
 * that hides only the newest features, from only the people who filter.
 */
export const AUDIT_ACTION_CATEGORY = {
    // Documents
    'invoice.issued': 'documents',
    'invoice.drafted': 'documents',
    'invoice.updated': 'documents',
    'invoice.voided': 'documents',
    // Chasing payment (0035): a reminder sent, a step skipped, a record undone.
    'invoice.reminded': 'documents',
    'invoice.reminder_skipped': 'documents',
    'invoice.reminder_undone': 'documents',
    /** When the money is expected changed (0039): forecast and reminders only, no figure. */
    'invoice.expectation_set': 'documents',
    /** The PDF went to the customer by email from Fint. WhatsApp and links leave the device, not the server, so they are not here. */
    'invoice.emailed': 'documents',
    /** Part of the balance discharged by a decision: rounding, or a debt given up on. */
    'invoice.written_off': 'documents',
    /** TDS recorded on a payment after the fact; the invoice it settles takes the deduction. */
    'payment.tds_recorded': 'money',
    'invoice.deleted': 'documents',
    // Subscriptions (0027). The drafts they raise are logged as ordinary
    // `invoice.drafted` rows carrying the profile and the period, because that
    // is what they are - a draft a timer made rather than a person.
    'recurring.created': 'documents',
    'recurring.updated': 'documents',
    'recurring.paused': 'documents',
    'recurring.deleted': 'documents',
    'proforma.issued': 'documents',
    'proforma.drafted': 'documents',
    'proforma.updated': 'documents',
    'proforma.voided': 'documents',
    'proforma.deleted': 'documents',
    'proforma.converted': 'documents',
    // A revision is not an edit: it is the moment a figure reached a customer.
    // Separate actions because "who changed the quote" and "what did we actually
    // send them, and when" are different questions asked by different people.
    'proforma.revised': 'documents',
    'proforma.revision_drafted': 'documents',
    'file.attached': 'documents',
    'file.deleted': 'documents',
    // Money
    'payment.recorded': 'money',
    'payment.deleted': 'money',
    /** Its account or amount corrected, with the reason in the person's words (payments had no edit before). */
    'payment.updated': 'money',
    'expense.created': 'money',
    /** Money back on a charge: a negative row in its category, and a deposit that is not income (0030). */
    'expense.refunded': 'money',
    'expense.drafted': 'money',
    'expense.confirmed': 'money',
    'expense.updated': 'money',
    'expense.deleted': 'money',
    'expense_category.created': 'money',
    'expense_category.updated': 'money',
    'income_category.created': 'money',
    'income_category.updated': 'money',
    // Re-labelling and re-projecting, which are permitted inside a FILED period
    // - see below and docs/for-accountants.md. Their own actions rather than
    // `expense.updated`, because "who moved this into that project after the
    // return was filed" is the question the exemption has to be able to answer.
    'expense.retagged': 'money',
    'expense.reprojected': 'money',
    /** Who an expense was paid to, set or changed - a label, no figure. */
    'expense.vendor_set': 'money',
    'invoice.retagged': 'documents',
    'invoice.reprojected': 'documents',
    'bank_transaction.income_categorised': 'money',
    'bank_account.created': 'money',
    'bank_account.updated': 'money',
    'bank_account.deactivated': 'money',
    'bank_account.deleted': 'money',
    'bank_transaction.created': 'money',
    // A proposed deposit and its confirmation - the bank-row half of what
    // `expense.drafted` / `expense.confirmed` are for spend. See migration 0015.
    'bank_transaction.drafted': 'money',
    'bank_transaction.confirmed': 'money',
    'bank_transaction.categorised': 'money',
    /** A person's own line corrected - date, amount, description or account - with the reason. */
    'bank_transaction.updated': 'money',
    'bank_transaction.excluded': 'money',
    // Who paid a direct receipt, and for what (0017). A classification, never
    // a figure: the amount, the date and the account are not on that path.
    'bank_transaction.attributed': 'money',
    'bank_transfer.created': 'money',
    'bank_transfer.undone': 'money',
    // The things documents are raised against
    'customer.created': 'records',
    'customer.updated': 'records',
    'customer.deleted': 'records',
    'customer.deactivated': 'records',
    // Vendors travel with customers: one service writes both, as
    // `${partyType}.created`. Missing them would leave every expense-side
    // counterparty change unlabelled and out of every filtered view - and a
    // template-literal action is exactly the kind a grep for labels misses.
    'vendor.created': 'records',
    'vendor.updated': 'records',
    'vendor.deleted': 'records',
    'vendor.deactivated': 'records',
    'tag.created': 'records',
    'tag.updated': 'records',
    'tag.archived': 'records',
    'tag.deleted': 'records',
    'saved_filter.created': 'records',
    'saved_filter.updated': 'records',
    'saved_filter.deleted': 'records',
    'rate.fetched': 'records',
    'rate.saved': 'records',
    'project.created': 'records',
    'project.updated': 'records',
    'project.deleted': 'records',
    'item.created': 'records',
    'item.updated': 'records',
    'item.deactivated': 'records',
    'item.deleted': 'records',
    /** A code written onto the lines that had none - changes what Table 12 says, not a figure. */
    'invoice.hsn_filled': 'records',
    /**
     * A draft item or customer (0017) and its confirmation - the catalogue
     * half of what `expense.drafted` / `expense.confirmed` are for spend. The
     * actor on `drafted` is the agent's key; on `confirmed` it is the person.
     */
    'item.drafted': 'records',
    'item.confirmed': 'records',
    'customer.drafted': 'records',
    'customer.confirmed': 'records',
    'vendor.drafted': 'records',
    'vendor.confirmed': 'records',
    // Filing
    // A review finding explained rather than fixed, and looked at again.
    'report.exported': 'filing',
    'review.set_aside': 'filing',
    'review.reopened': 'filing',
    'period.locked': 'filing',
    // Filing rather than admin: "who moved the target the month we missed it" is
    // the same shape of question as "who unlocked the period", and burying it
    // among role edits answers neither.
    'target.updated': 'filing',
    'period.unlocked': 'filing',
    // Declarations made to the bank under FEMA (0032): the month's EDF, and an
    // export invoice's EDPMS entry closed or its realisation extended.
    'export.edf_recorded': 'filing',
    'export.edf_removed': 'filing',
    'export.closure_recorded': 'filing',
    'tax.created': 'filing',
    'tax.updated': 'filing',
    'tax.gst_rates_seeded': 'filing',
    // The CFO tab's dues calendar (0036): a statutory due marked filed or paid
    // outside Fint, and the mark taken back.
    'cfo.due_marked': 'filing',
    'cfo.due_unmarked': 'filing',
    // The CFO tab (0035): its settings, and the debts a person entered (0037).
    'cfo.settings_updated': 'admin',
    'debt.created': 'money',
    'debt.updated': 'money',
    'debt.removed': 'money',
    'account.deletion_requested': 'admin',
    'account.deletion_cancelled': 'admin',
    'account.deleted': 'admin',
    'account.book_downloaded': 'admin',
    // People and configuration
    'member.invited': 'admin',
    'member.updated': 'admin',
    'member.removed': 'admin',
    'role.created': 'admin',
    'role.updated': 'admin',
    'role.deleted': 'admin',
    'api_key.created': 'admin',
    'api_key.revoked': 'admin',
    'settings.updated': 'admin',
    // Written into the NEW book's trail: its first entry is its own creation.
    'org.created': 'admin',
    'org.updated': 'admin',
    // The organisation above the book (0022): written into the book's trail
    // when the book moves; the organisation's own changes go to every book in
    // it. Creating one writes nothing - there is no book to write into yet;
    // the first book made in it says `org.created` as every book does.
    'organisation.updated': 'admin',
    'book.moved': 'admin',
    // Claude
    'mcp.tool_called': 'assistant',
    'mcp.tool_failed': 'assistant',
    /**
     * Connecting an assistant, and disconnecting one.
     *
     * Under `assistant` rather than `admin` because that is the question these
     * answer - "what has an assistant been allowed to see" - and because the
     * Activity page leaves that category off unless it is asked for, which is
     * right for a trail people read to find figures changing.
     *
     * `oauth.refresh_reused` is the one nobody wants to see. It means a refresh
     * token was presented twice, which means two parties hold it, and Fint
     * revoked the whole connection rather than guess which one was the owner.
     */
    'oauth.granted': 'assistant',
    'oauth.revoked': 'assistant',
    'oauth.refresh_reused': 'assistant',
    // Bulk classification writes one of these per record it recategorised. Its
    // own action rather than `expense.updated` for the reason `retagged` and
    // `reprojected` have theirs: a category decides which bucket money is in,
    // and "who moved four hundred expenses into Travel" has to be answerable.
    'expense.recategorised': 'money',
    /**
     * The Inbox: a model proposed, a person decided.
     *
     * Under `money` rather than `assistant`, because the actor is the PERSON.
     * `mcp.*` records what Claude did; these record what somebody did about it,
     * and "who confirmed the 140 rows Claude read off that spreadsheet" is a
     * question about a figure moving, asked by the person who reads this group.
     * The per-record action (`expense.confirmed`, `bank_transaction.confirmed`)
     * is written beside each one, so a reader following one record still finds
     * it under its own name.
     */
    'proposal_batch.created': 'money',
    'proposal.confirmed': 'money',
    /** An agent corrected its own draft before anybody confirmed it. */
    'proposal.revised': 'money',
    'proposal.discarded': 'money',
    /** A model took back a row it had proposed, with its reason. */
    'proposal.withdrawn': 'money',
    /** Written on BOTH books: the one it left and the one it landed in. */
    'proposal.moved': 'money',
    /**
     * A proposed CHANGE to a record that exists (0017): the agent proposed it
     * (`created`, actor the key), a person applied it (`applied`, actor the
     * person, with the before and after in the metadata). A discarded change
     * is `proposal.discarded` like every other kind.
     */
    'proposal_change.created': 'money',
    'proposal.applied': 'money',
    /**
     * The migration kit (0018). An invoice recreated from another system is
     * proposed (`invoice.imported`, actor the agent's key - the invoice is a
     * draft, in no figure, until the Inbox confirms it, and THAT confirm is
     * written as `invoice.issued` with `imported: true` in the metadata, actor
     * the person). A settlement - "this invoice was paid by that deposit" -
     * proposed (`settlement.proposed`, actor the key) and, when applied,
     * `payment.recorded` beside `proposal.applied`, both by the person.
     */
    'invoice.imported': 'documents',
    'settlement.proposed': 'money',
    /** A person matched a deposit to an invoice themselves, in Fint - the settlement applied at once. */
    'settlement.recorded': 'money',
};
export const ALL_KNOWN_ACTIONS = Object.keys(AUDIT_ACTION_CATEGORY);
/**
 * Human phrasing, written to follow an actor's name: "Priya voided an invoice".
 *
 * Past tense, and deliberately without the record's name - the row renders that
 * separately, so putting it here would print it twice.
 */
export const AUDIT_ACTION_LABEL = {
    'invoice.issued': 'issued an invoice',
    // Saved, not issued: on no return and in no receivable until somebody sends it.
    'invoice.drafted': 'saved a draft invoice',
    'invoice.updated': 'edited an invoice',
    'invoice.voided': 'voided an invoice',
    'invoice.emailed': 'emailed an invoice to the customer',
    'invoice.written_off': 'wrote off the remainder of an invoice',
    'payment.tds_recorded': 'recorded TDS on a payment',
    'invoice.deleted': 'deleted a draft invoice',
    'recurring.created': 'set up a subscription',
    'recurring.updated': 'changed a subscription',
    // Written by the scan itself, with the refusal that caused it in the
    // metadata - a filed period is the usual one.
    'recurring.paused': 'paused a subscription',
    'recurring.deleted': 'deleted a subscription',
    'proforma.issued': 'issued a proforma',
    'proforma.drafted': 'saved a draft proforma',
    'proforma.updated': 'edited a proforma',
    'proforma.voided': 'voided a proforma',
    'proforma.deleted': 'deleted a draft proforma',
    'proforma.converted': 'converted a proforma to an invoice',
    // Sent, not saved. The revision number is in the metadata, so the row reads
    // "Priya sent a new revision of PI-000012" once the page joins the two.
    'proforma.revised': 'sent a new revision of a proforma',
    // Claude's edit to the working copy. It reached no customer: only a person
    // pressing Send does that, and no tool can.
    'proforma.revision_drafted': 'drafted changes to a proforma',
    'file.attached': 'attached a file',
    'file.deleted': 'removed an attachment',
    'payment.recorded': 'recorded a payment',
    'payment.deleted': 'deleted a payment',
    'payment.updated': 'corrected a payment',
    'expense.created': 'added an expense',
    'expense.refunded': 'recorded a refund on an expense',
    // Proposed, not spent: in no total until somebody confirms it.
    'expense.drafted': 'drafted an expense from a bill',
    'expense.confirmed': 'confirmed a drafted expense',
    'expense.updated': 'edited an expense',
    'expense.deleted': 'deleted an expense',
    'expense_category.created': 'added an expense category',
    'expense_category.updated': 'edited an expense category',
    'income_category.created': 'added an income category',
    'income_category.updated': 'edited an income category',
    'expense.retagged': 'changed the tags on an expense',
    'expense.reprojected': 'moved an expense to a different project',
    'expense.vendor_set': 'said who an expense was paid to',
    'invoice.retagged': 'changed the tags on an invoice',
    'invoice.reprojected': 'moved an invoice to a different project',
    'bank_transaction.income_categorised': 'said what kind of income a deposit was',
    'bank_account.created': 'added a bank account',
    'bank_account.updated': 'edited a bank account',
    'bank_account.deactivated': 'deactivated a bank account',
    'bank_account.deleted': 'deleted a bank account',
    'bank_transaction.created': 'added a bank transaction',
    // Proposed, not received: in no balance until somebody confirms it.
    'bank_transaction.drafted': 'drafted a deposit',
    'bank_transaction.confirmed': 'confirmed a drafted deposit',
    'bank_transaction.categorised': 'categorised a bank transaction',
    'bank_transaction.updated': 'corrected a bank line',
    'bank_transaction.excluded': 'excluded a bank transaction',
    'bank_transaction.attributed': 'said who paid a deposit and what it was for',
    'bank_transfer.created': 'transferred between accounts',
    'bank_transfer.undone': 'undid a transfer between accounts',
    'customer.created': 'added a customer',
    'customer.updated': 'edited a customer',
    'customer.deleted': 'deleted a customer',
    'customer.deactivated': 'archived a customer',
    'vendor.created': 'added a vendor',
    'vendor.updated': 'edited a vendor',
    'vendor.deleted': 'deleted a vendor',
    'vendor.deactivated': 'archived a vendor',
    'tag.created': 'added a tag',
    'tag.updated': 'renamed or recoloured a tag',
    'tag.archived': 'archived a tag',
    'tag.deleted': 'deleted a tag',
    'saved_filter.created': 'saved a filter',
    'saved_filter.updated': 'changed a saved filter',
    'saved_filter.deleted': 'deleted a saved filter',
    'rate.fetched': 'fetched an exchange rate',
    'rate.saved': 'saved an exchange rate',
    'project.created': 'created a project',
    'project.updated': 'edited a project',
    'project.deleted': 'deleted a project',
    'item.created': 'added an item',
    'item.updated': 'edited an item',
    'item.deactivated': 'archived an item',
    'item.deleted': 'deleted an item',
    'invoice.hsn_filled': 'filled the HSN/SAC on invoice lines that had none',
    // Proposed, not in the catalogue: in no picker until somebody confirms it.
    'item.drafted': 'drafted a catalogue item',
    'item.confirmed': 'confirmed a drafted item',
    'customer.drafted': 'drafted a customer',
    'customer.confirmed': 'confirmed a drafted customer',
    'vendor.drafted': 'drafted a vendor',
    'vendor.confirmed': 'confirmed a drafted vendor',
    // The two an accountant looks for first, so they say what actually happened
    // rather than naming the verb.
    'report.exported': 'exported a report pack',
    'review.set_aside': 'set a review finding aside with a reason',
    'review.reopened': 'brought a set-aside review finding back',
    'period.locked': 'locked a period for filing',
    'period.unlocked': 'reopened a locked period',
    'export.edf_recorded': 'recorded a month’s Export Declaration Form',
    'export.edf_removed': 'took back a recorded Export Declaration Form',
    'export.closure_recorded': 'recorded an export invoice’s EDPMS closure',
    'cfo.due_marked': 'marked a tax due as filed or paid',
    'cfo.due_unmarked': 'took back a tax due marked done',
    'cfo.settings_updated': 'changed the CFO settings',
    'debt.created': 'added a debt',
    'debt.updated': 'changed a debt',
    'debt.removed': 'removed a debt',
    'account.deletion_requested': 'asked to delete their Fint account',
    'account.deletion_cancelled': 'cancelled deleting their Fint account',
    'account.deleted': 'deleted their Fint account and left this book',
    'account.book_downloaded': 'downloaded the whole book',
    'invoice.reminded': 'sent a payment reminder',
    'invoice.reminder_skipped': 'skipped a payment reminder',
    'invoice.reminder_undone': 'took back a recorded payment reminder',
    'invoice.expectation_set': 'changed when an invoice is expected to be paid',
    'tax.created': 'added a tax rate',
    'tax.updated': 'changed a tax rate',
    'tax.gst_rates_seeded': 'switched on GST and seeded the rates',
    'target.updated': 'changed a monthly target',
    'member.invited': 'invited someone',
    'member.updated': 'changed someone’s role',
    'member.removed': 'removed someone',
    'role.created': 'created a role',
    'role.updated': 'changed what a role can do',
    'role.deleted': 'deleted a role',
    'api_key.created': 'created an API key',
    'api_key.revoked': 'revoked an API key',
    'settings.updated': 'changed the settings',
    'org.created': 'created this book',
    'org.updated': 'renamed or recoloured this book',
    'organisation.updated': 'renamed, recoloured or handed over the organisation',
    'book.moved': 'moved this book to another organisation',
    'mcp.tool_called': 'read data through an AI agent',
    // Not a refusal - a refusal is a `mcp.tool_called` whose answer was "no".
    // This is the request itself breaking, which is rare and worth noticing.
    'mcp.tool_failed': 'hit an error reading data through an AI agent',
    'oauth.granted': 'connected an assistant to this business',
    'oauth.revoked': 'disconnected an assistant',
    // Phrased as what Fint did, not what the actor did, because the actor here is
    // the grant's owner and they did nothing - somebody replayed their token.
    'oauth.refresh_reused': 'had an assistant connection revoked after a token was reused',
    'expense.recategorised': 'changed the category on an expense',
    'proposal_batch.created': 'received a batch of proposals from an AI agent',
    'proposal.confirmed': 'confirmed a proposal from the Inbox',
    'proposal.revised': 'revised a proposed draft',
    'proposal.discarded': 'discarded a proposal from the Inbox',
    'proposal.withdrawn': 'withdrew a proposal it had made',
    'proposal.moved': 'moved a proposal to another book',
    'proposal_change.created': 'proposed a change to a record',
    'proposal.applied': 'applied a proposed change from the Inbox',
    // Proposed, not issued: a draft with the old system's number beside it.
    'invoice.imported': 'imported an invoice from another system as a draft',
    'settlement.proposed': 'proposed settling an invoice with a deposit that already arrived',
    'settlement.recorded': 'matched a deposit to an invoice',
};
/**
 * A readable phrase, always.
 *
 * The fallback turns `some.new_action` into "some new action" rather than
 * showing the slug, so an action added without a label reads as slightly terse
 * English instead of as something broken.
 */
export function labelForAction(action) {
    return AUDIT_ACTION_LABEL[action] ?? action.replace(/[._]/g, ' ');
}
/**
 * Expand chosen categories into the actions to filter on.
 *
 * Null means "no filter": nothing selected, or everything selected. Callers
 * skip the predicate entirely rather than building an `IN` list of every action
 * that exists.
 *
 * `excludeUnknown` is the escape hatch, and it is set only when `admin` is
 * among the selection. `admin` is where an unrecognised action falls, so
 * selecting it has to mean "the admin actions, OR anything I have never heard
 * of" - otherwise an action added next month would belong to no category and
 * be invisible in every filtered view, silently, and only for the newest
 * features.
 */
export function actionsForCategories(categories) {
    if (!categories?.length)
        return null;
    const selected = categories.filter((c) => AUDIT_CATEGORIES.some((known) => known.key === c));
    if (!selected.length || selected.length === AUDIT_CATEGORIES.length)
        return null;
    return {
        include: ALL_KNOWN_ACTIONS.filter((action) => selected.includes(AUDIT_ACTION_CATEGORY[action])),
        excludeUnknown: selected.includes('admin'),
    };
}
//# sourceMappingURL=audit-actions.js.map